import { useEffect } from 'react';

import { useFormFieldContext } from './useFormFieldContext.js';
import { useFormFieldMessagesContext } from './useFormFieldMessagesContext.js';
import { useId } from '../../../core/hooks/useId.js';
import { parseBoolean as _parseBoolean } from '../../../core/utils/parse-boolean.js';
import { FormFieldMessagesAction } from '../state/formfield-messages-state-reducer.js';
import { FormFieldAction } from '../state/formfield-state-reducer.js';

/**
 * Keeps disabled, aria-disabled, required and id in sync with the formField context.
 * It also creates a unique id in case non was provided so far.
 * @internal
 */
export function useSyncedFormControlProps(formcontrolprops: {
  propDisabled?: boolean;
  propAriaDisabled?: boolean | 'true' | 'false';
  propRequired?: boolean;
  propId?: string;
  propReadOnly?: boolean;
}) {
  const uniqueId = useId();
  const { propDisabled, propRequired, propId, propAriaDisabled, propReadOnly } =
    formcontrolprops;

  const {
    controlId,
    required: contextRequired,
    disabled: contextDisabled,
    ariaDisabled: contextAriaDisabled,
    readOnly: contextReadOnly,
    dispatch,
  } = useFormFieldContext();

  const { dispatch: formFieldMessageContextDispatch } =
    useFormFieldMessagesContext();

  const disabled = propDisabled ?? contextDisabled;
  const ariaDisabled = _parseBoolean(propAriaDisabled) ?? contextAriaDisabled;
  const required = propRequired ?? contextRequired;
  const readOnly = propReadOnly ?? contextReadOnly;
  const id = propId || controlId || uniqueId;

  // we are using the useEffect here to not run into a bad state error
  useEffect(() => {
    dispatch?.({
      type: FormFieldAction.UPDATE,
      payload: {
        id: propId,
        disabled: propDisabled,
        required: propRequired,
        ariaDisabled: _parseBoolean(propAriaDisabled),
        readOnly: propReadOnly,
      },
    });
  }, [
    dispatch,
    propDisabled,
    propId,
    propRequired,
    propAriaDisabled,
    propReadOnly,
  ]);

  useEffect(() => {
    formFieldMessageContextDispatch?.({
      type: FormFieldMessagesAction.DISABLEMESSAGE,
      payload: { isDisabled: disabled },
    });
  }, [disabled, formFieldMessageContextDispatch]);

  return {
    disabled,
    required,
    ariaDisabled,
    id,
    readOnly,
  };
}
